import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import KeaktifanBars, { toBarItems } from "@/components/KeaktifanBars";
import api from "@/lib/api";
import { downloadFile } from "@/lib/upload";
import { StatusBadge, initials, PERIODS, periodRange, LOGO_URL, APP_NAME } from "@/components/common";
import StatCard from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { QrButtons } from "@/components/admin/QrTools";
import CatatanPengasuh from "@/components/admin/CatatanPengasuh";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, FileDown, BookOpenText, CalendarCheck, ShieldAlert, Award, Pencil, Trash2, IdCard, GraduationCap, Building2 } from "lucide-react";

const MONTHS = [
  { v: "all", label: "Semua Bulan" },
  { v: "2026-07", label: "Juli 2026" }, { v: "2026-08", label: "Agustus 2026" },
  { v: "2026-09", label: "September 2026" }, { v: "2026-10", label: "Oktober 2026" },
  { v: "2026-11", label: "November 2026" }, { v: "2026-12", label: "Desember 2026" },
  { v: "2027-01", label: "Januari 2027" }, { v: "2027-02", label: "Februari 2027" },
  { v: "2027-03", label: "Maret 2027" }, { v: "2027-04", label: "April 2027" },
  { v: "2027-05", label: "Mei 2027" }, { v: "2027-06", label: "Juni 2027" },
];

const _BULAN = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const monthLabel = (ym) => { const [y, m] = (ym || "").split("-"); return `${_BULAN[+m] || m} ${y}`; };

function MonthlyAkumulasi({ s, absensi }) {
  const daily = {};
  (absensi || []).forEach((a) => {
    const ym = (a.tanggal || "").slice(0, 7); if (!ym) return;
    const k = `${a.kategori}|${ym}`; (daily[k] = daily[k] || { h: 0, t: 0 }); daily[k].t++; if (a.status === "hadir") daily[k].h++;
  });
  const cats = [["ngaji_dhuha", "ngaji_bulanan", "Ngaji Dhuha"], ["keaktifan_departemen", "departemen_bulanan", "Keaktifan Departemen"], ["keaktifan_ribath", "ribath_bulanan", "Keaktifan Ribath"], ["kuliah", "kuliah_bulanan", "Keaktifan Kuliah"]];
  const label = (ym) => { const [y, m] = ym.split("-"); return `${_BULAN[+m] || m} ${y}`; };
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="riwayat-bulanan">
      {cats.map(([kat, bf, title]) => {
        const bmap = s[bf] || {};
        const months = Array.from(new Set([...Object.keys(bmap), ...Object.keys(daily).filter((k) => k.startsWith(kat + "|")).map((k) => k.split("|")[1])])).sort().reverse();
        return (
          <div key={kat} className="bg-white/60 dark:bg-white/5 rounded-xl p-4" data-testid={`akum-${kat}`}>
            <p className="font-semibold text-emerald-900 dark:text-emerald-100 mb-2">{title}</p>
            {months.length === 0 ? <p className="text-xs text-slate-400">Belum ada data.</p> : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {months.map((ym) => {
                  const d = daily[`${kat}|${ym}`];
                  let txt, pct;
                  if (d && d.t > 0) { pct = Math.round((d.h / d.t) * 100); txt = `Hadir ${d.h} dari ${d.t} pertemuan`; }
                  else { const v = bmap[ym]; pct = typeof v === "number" ? Math.round(v) : null; txt = "rekap bulanan"; }
                  return (
                    <div key={ym} className="flex items-center justify-between text-sm gap-2">
                      <span className="text-slate-600 dark:text-slate-300 truncate">{label(ym)}<span className="text-[11px] text-slate-400 ml-1">· {txt}</span></span>
                      <span className="font-bold text-emerald-700 dark:text-emerald-300 shrink-0">{pct == null ? "-" : pct + "%"}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function SantriDetail({ santriId, onBack }) {
  const qc = useQueryClient();
  const { user, hasPerm } = useAuth();
  const canEditAbs = hasPerm("absensi.write");
  const isSuper = user?.role === "super_admin";
  const canEditPel = hasPerm("pelanggaran.manage");
  const [period, setPeriod] = useState("all");
  const [editRec, setEditRec] = useState(null); // {type:'absensi'|'pelanggaran', data}

  const range = period === "all" ? {} : (() => {
    const [y, m] = period.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return { start: `${period}-01`, end: `${period}-${String(last).padStart(2, "0")}` };
  })();
  const { data: summary, refetch } = useQuery({
    queryKey: ["summary", santriId, period],
    queryFn: async () => (await api.get(`/santri/${santriId}/summary`, { params: range })).data,
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["summary", santriId] }); qc.invalidateQueries({ queryKey: ["santri"] }); refetch(); };

  const delKeaktifan = useMutation({
    mutationFn: async ({ kategori, bulan }) => api.delete("/absensi/keaktifan-persen", { params: { santri_id: santriId, kategori, bulan } }),
    onSuccess: () => { toast.success("Data keaktifan bulan tersebut dihapus"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal menghapus"),
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (editRec.type === "absensi") return api.put(`/absensi/${editRec.data.id}`, editRec.data);
      return api.put(`/pelanggaran/${editRec.data.id}`, editRec.data);
    },
    onSuccess: () => { toast.success("Data diperbarui"); setEditRec(null); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal"),
  });
  const delAbs = useMutation({ mutationFn: async (id) => api.delete(`/absensi/${id}`), onSuccess: () => { toast.success("Absensi dihapus"); invalidate(); } });
  const delPel = useMutation({ mutationFn: async (id) => api.delete(`/pelanggaran/${id}`), onSuccess: () => { toast.success("Pelanggaran dihapus"); invalidate(); } });

  if (!summary) return <div className="glass rounded-2xl p-10 text-center text-slate-400">Memuat detail santri...</div>;
  const s = summary.santri;

  const barItems = toBarItems(summary);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" className="gap-2" data-testid="back-to-santri-list" onClick={onBack}><ArrowLeft className="h-4 w-4" /> Kembali</Button>
        <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:w-auto sm:items-center">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="col-span-2 w-full sm:w-40 rounded-xl bg-white/60 dark:bg-white/5" data-testid="detail-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" className="w-full sm:w-auto gap-2 rounded-xl bg-white/60 dark:bg-white/5" data-testid="export-santri-button" onClick={() => downloadFile(`/export/santri/${santriId}`, `rekap_${s.no_induk_pondok}.xlsx`)}><FileDown className="h-4 w-4" /> Ekspor</Button>
          <Button className="w-full sm:w-auto gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="print-kts-button" onClick={() => downloadFile(`/santri/${santriId}/kts-pdf`, `KTS_${s.no_induk_pondok}.pdf`)}><IdCard className="h-4 w-4" /> Cetak E-KTS</Button>
          <QrButtons santri={s} />
        </div>
      </div>

      {/* E-KTS: Kartu Tanda Santri */}
      <div className="grid gap-6 lg:grid-cols-5" data-testid="ekts-card">
        <div className="lg:col-span-3">
          <div className="relative overflow-hidden rounded-3xl p-6 text-white shadow-xl"
            style={{ background: "linear-gradient(135deg, #173404 0%, #3B6D11 55%, #639922 100%)" }}>
            <div className="absolute -top-16 -right-12 h-56 w-56 rounded-full bg-emerald-400/20 blur-3xl" />
            <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-teal-300/10 blur-3xl" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <img src={LOGO_URL} alt="Logo" className="h-9 w-9 object-contain drop-shadow" />
                  <div className="leading-tight">
                    <p className="font-bold text-sm tracking-wide">{APP_NAME}</p>
                    <p className="text-[11px] text-emerald-200">Kartu Tanda Santri (E-KTS)</p>
                  </div>
                </div>
                <span className={`text-[11px] px-2.5 py-1 rounded-full font-semibold ${s.keterangan === "khos" ? "bg-amber-400/95 text-amber-950" : s.keterangan === "boyong" ? "bg-rose-500/95 text-white" : s.keterangan === "diboyong" ? "bg-slate-300/95 text-slate-800" : "bg-emerald-400/90 text-emerald-950"}`}>{(s.keterangan || "aktif").toUpperCase()}</span>
              </div>

              <div className="flex items-center gap-4">
                <Avatar className="h-24 w-24 rounded-2xl ring-2 ring-white/40 shadow-lg">
                  <AvatarImage src={s.foto} className="object-cover" />
                  <AvatarFallback className="bg-emerald-200 text-emerald-800 text-2xl rounded-2xl">{initials(s.nama)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl font-extrabold leading-tight break-words" data-testid="ekts-nama">{s.nama}</h2>
                  <p className="text-emerald-200 text-sm font-mono">No. Induk {s.no_induk_pondok}</p>
                  <p className="text-emerald-100/80 text-xs mt-1">NIDI {s.nidi || "-"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-5 gap-y-3 mt-6 text-sm">
                <KtsField label="Departemen" value={s.departemen} />
                <KtsField label="Ribath" value={s.ribath} />
                <KtsField label="Semester" value={s.smt} />
                <KtsField label="Prodi / Ket." value={s.prodi_ket} />
                <KtsField label="Status" value={s.status} />
                <KtsField label="Nama Wali" value={s.nama_wali} />
                {"nomor_wa_wali" in s && <KtsField label="No. WA Wali" value={s.nomor_wa_wali} />}
                {"alamat" in s && <KtsField label="Alamat" value={s.alamat} />}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <KeaktifanBars items={barItems} average={summary.rata_rata_keseluruhan} title="Rekap Keaktifan" subtitle={MONTHS.find((p) => p.v === period)?.label} testId="ekts-bars" />
        </div>
      </div>

      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard icon={BookOpenText} label="Keaktifan Ngaji Dhuha" value={barItems[0].hasData ? `${summary.ngaji_dhuha.persen}%` : "Belum ada data"} sub={summary.ngaji_dhuha.dari_sheet ? "dari Sheet" : `${summary.ngaji_dhuha.hadir}/${summary.ngaji_dhuha.total}`} tone="emerald" />
        <StatCard icon={Building2} label="Keaktifan Departemen" value={barItems[1].hasData ? `${summary.keaktifan_departemen.persen}%` : "Belum ada data"} sub={summary.keaktifan_departemen.dari_sheet ? "dari Sheet" : `${summary.keaktifan_departemen.hadir}/${summary.keaktifan_departemen.total}`} tone="teal" />
        <StatCard icon={CalendarCheck} label="Keaktifan Ribath" value={barItems[2].hasData ? `${summary.keaktifan_ribath.persen}%` : "Belum ada data"} sub={summary.keaktifan_ribath.dari_sheet ? "dari Sheet" : `${summary.keaktifan_ribath.hadir}/${summary.keaktifan_ribath.total}`} tone="teal" />
        <StatCard icon={GraduationCap} label="Keaktifan Kuliah" value={barItems[3].hasData ? `${summary.keaktifan_kuliah?.persen ?? 0}%` : "Belum ada data"} sub="dari Sheet" tone="amber" />
        <StatCard icon={ShieldAlert} label="Pelanggaran" value={summary.jumlah_pelanggaran} sub={`${summary.total_poin_pelanggaran} poin`} tone="rose" />
        <StatCard icon={Award} label="Sisa Poin" value={s.poin} sub={s.poin >= 90 ? "Baik" : s.poin >= 70 ? "Cukup" : "Perhatian"} tone="amber" />
      </div>

      {summary.motivasi?.pesan && (
        <div className="glass rounded-2xl p-5 border-l-4 border-emerald-500" data-testid="motivasi-box">
          <p className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-400 font-semibold">Catatan Motivasi · Rata-rata Keseluruhan {summary.rata_rata_keseluruhan}%</p>
          <p className="text-emerald-900 dark:text-emerald-100 mt-1">{summary.motivasi.pesan}</p>
        </div>
      )}

      {/* Riwayat kehadiran */}
      {isSuper ? (
      <div className="glass rounded-2xl p-5">
        <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-3">Riwayat Absensi Harian Ngaji Dhuha ({summary.absensi.filter((a) => a.kategori === "ngaji_dhuha").length}) <span className="text-xs font-normal text-slate-400 dark:text-slate-500">— khusus Super Admin, untuk koreksi entri harian (keaktifan bulanan ada di bawah)</span></h3>
        <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Kategori</TableHead><TableHead>Kegiatan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {summary.absensi.filter((a) => a.kategori === "ngaji_dhuha").map((a) => (
                <TableRow key={a.id} data-testid={`abs-row-${a.id}`}>
                  <TableCell className="text-slate-500 dark:text-slate-300">{a.tanggal}</TableCell>
                  <TableCell>{a.kategori === "ngaji_dhuha" ? "Ngaji Dhuha" : a.kategori === "keaktifan_departemen" ? "Keaktifan Departemen" : "Keaktifan Ribath"}</TableCell>
                  <TableCell>{a.nama_kegiatan || "-"}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  <TableCell className="text-right">
                    {canEditAbs && <><Button variant="ghost" size="icon" data-testid={`edit-abs-${a.id}`} onClick={() => setEditRec({ type: "absensi", data: { ...a } })}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`del-abs-${a.id}`} onClick={() => delAbs.mutate(a.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button></>}
                  </TableCell>
                </TableRow>
              ))}
              {summary.absensi.filter((a) => a.kategori === "ngaji_dhuha").length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Belum ada data ngaji dhuha harian.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
      ) : (
      <div className="glass rounded-2xl p-5">
        <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-3">Akumulasi Kehadiran per Bulan</h3>
        <MonthlyAkumulasi s={s} absensi={summary.absensi} />
      </div>
      )}

      {/* Riwayat Keaktifan Bulanan (departemen & ribath) — rekap bulanan, bisa dihapus */}
      {canEditAbs && (
        <div className="glass rounded-2xl p-5" data-testid="riwayat-keaktifan-bulanan">
          <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-1">Riwayat Keaktifan Bulanan (Departemen &amp; Ribath)</h3>
          <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">Keaktifan departemen/ribath disimpan sebagai rekap bulanan (dari Sheet), bukan absensi harian — karena itu tidak muncul di tabel harian di atas. Hapus di sini bila ingin menghapus data uji.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {[["keaktifan_departemen", "departemen", "Keaktifan Departemen"], ["keaktifan_ribath", "ribath", "Keaktifan Ribath"]].map(([kat, tkey, title]) => {
              const bmap = summary.tren_bulanan?.[tkey] || {};
              const months = Object.keys(bmap).sort().reverse();
              return (
                <div key={kat} className="bg-white/60 dark:bg-white/5 rounded-xl p-4" data-testid={`keaktifan-${tkey}`}>
                  <p className="font-semibold text-emerald-900 dark:text-emerald-100 mb-2">{title}</p>
                  {months.length === 0 ? <p className="text-xs text-slate-400">Belum ada data.</p> : (
                    <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                      {months.map((ym) => (
                        <div key={ym} className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-slate-600 dark:text-slate-300 truncate">{monthLabel(ym)}</span>
                          <span className="flex items-center gap-2 shrink-0">
                            <span className="font-bold text-emerald-700 dark:text-emerald-300">{Math.round(bmap[ym])}%</span>
                            <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`del-keaktifan-${tkey}-${ym}`} onClick={() => { if (window.confirm(`Hapus data ${title} bulan ${monthLabel(ym)}? Nilai di aplikasi dihapus & kolom bulan itu di Sheet di-nol-kan.`)) delKeaktifan.mutate({ kategori: kat, bulan: ym }); }}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pelanggaran history */}
      <div className="glass rounded-2xl p-5">
        <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-3">Riwayat Pelanggaran ({summary.pelanggaran.length})</h3>
        <div className="space-y-2">
          {summary.pelanggaran.map((p) => (
            <div key={p.id} className="flex items-start justify-between gap-3 bg-white/50 dark:bg-white/5 rounded-xl p-3" data-testid={`pel-row-${p.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap"><p className="font-medium text-emerald-950 dark:text-emerald-50">{p.jenis}</p><StatusBadge status={p.kategori} /><span className="text-xs font-bold text-rose-600 dark:text-rose-400">-{p.poin}</span></div>
                <p className="text-xs text-slate-500 dark:text-slate-300 mt-0.5">{p.keterangan || "-"}{p.penanganan ? ` · ${p.penanganan}` : ""}</p>
                <p className="text-xs text-slate-400">{p.tanggal}</p>
              </div>
              {canEditPel && <div className="shrink-0">
                <Button variant="ghost" size="icon" data-testid={`edit-pel-${p.id}`} onClick={() => setEditRec({ type: "pelanggaran", data: { ...p } })}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" data-testid={`del-pel-${p.id}`} onClick={() => delPel.mutate(p.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>}
            </div>
          ))}
          {summary.pelanggaran.length === 0 && <p className="text-center text-slate-400 py-6 text-sm">Tidak ada catatan pelanggaran.</p>}
        </div>
      </div>

      {hasPerm("catatan.write") && <CatatanPengasuh santriId={santriId} />}

      {/* Edit record dialog */}
      <Dialog open={!!editRec} onOpenChange={(o) => !o && setEditRec(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit {editRec?.type === "absensi" ? "Absensi" : "Pelanggaran"}</DialogTitle></DialogHeader>
          {editRec?.type === "absensi" && (
            <div className="space-y-3 py-2">
              <div className="space-y-1.5"><Label className="text-xs">Tanggal</Label><Input type="date" value={editRec.data.tanggal} onChange={(e) => setEditRec({ ...editRec, data: { ...editRec.data, tanggal: e.target.value } })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Kegiatan</Label><Input value={editRec.data.nama_kegiatan} onChange={(e) => setEditRec({ ...editRec, data: { ...editRec.data, nama_kegiatan: e.target.value } })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Status</Label>
                <Select value={editRec.data.status} onValueChange={(v) => setEditRec({ ...editRec, data: { ...editRec.data, status: v } })}>
                  <SelectTrigger data-testid="edit-abs-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{["hadir", "izin", "sakit", "alpa"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          {editRec?.type === "pelanggaran" && (
            <div className="space-y-3 py-2">
              <div className="space-y-1.5"><Label className="text-xs">Jenis</Label><Input value={editRec.data.jenis} onChange={(e) => setEditRec({ ...editRec, data: { ...editRec.data, jenis: e.target.value } })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Kategori</Label>
                  <Select value={editRec.data.kategori} onValueChange={(v) => setEditRec({ ...editRec, data: { ...editRec.data, kategori: v } })}>
                    <SelectTrigger data-testid="edit-pel-kategori"><SelectValue /></SelectTrigger>
                    <SelectContent>{["ringan", "sedang", "berat"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Poin</Label><Input type="number" value={editRec.data.poin} onChange={(e) => setEditRec({ ...editRec, data: { ...editRec.data, poin: +e.target.value } })} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Keterangan</Label><Input value={editRec.data.keterangan || ""} onChange={(e) => setEditRec({ ...editRec, data: { ...editRec.data, keterangan: e.target.value } })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditRec(null)}>Batal</Button><Button data-testid="save-record-button" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="bg-emerald-700 hover:bg-emerald-800">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

function KtsField({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-emerald-300/80">{label}</p>
      <p className="font-medium break-words">{value || "-"}</p>
    </div>
  );
}
